// Phase 7 R1 — Create PO dialog launched from the Parts tab.
// Pre-fills lines from selected parts_lines; user can edit qty/unit_cost.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCreatePurchaseOrder } from '@/hooks/purchasing/useCreatePurchaseOrder';
import { toast } from '@/hooks/use-toast';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  selectedLines: PartsLine[];
  onSuccess?: () => void;
}

interface DraftLine {
  parts_line_id: string;
  description: string;
  vendor_part_number: string | null;
  quantity_ordered: string;
  unit_cost_ordered: string;
  core_charge_ordered: string;
  job_id: string | null;
}

export function CreatePurchaseOrderDialog({ open, onOpenChange, ticketId, selectedLines, onSuccess }: Props) {
  const navigate = useNavigate();
  const createMut = useCreatePurchaseOrder();

  const [vendorName, setVendorName] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);

  // Reset on open with current selection.
  useEffect(() => {
    if (!open) return;
    setVendorName(selectedLines[0]?.vendor_name ?? '');
    setExpectedAt('');
    setNotes('');
    setDraftLines(
      selectedLines.map((l) => ({
        parts_line_id: l.id,
        description: l.description,
        vendor_part_number: l.vendor_part_number,
        quantity_ordered: String(l.quantity ?? 1),
        unit_cost_ordered: l.unit_cost == null ? '' : String(l.unit_cost),
        core_charge_ordered: String(l.core_charge ?? 0),
        job_id: l.job_id,
      })),
    );
  }, [open, selectedLines]);

  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setDraftLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const total = draftLines.reduce((sum, l) => {
    const q = Number(l.quantity_ordered) || 0;
    const c = Number(l.unit_cost_ordered) || 0;
    return sum + q * c;
  }, 0);

  const handleSubmit = async () => {
    if (!vendorName.trim()) {
      toast({ title: 'Vendor name required', variant: 'destructive' });
      return;
    }
    if (draftLines.length === 0) {
      toast({ title: 'No lines to order', variant: 'destructive' });
      return;
    }
    // Validate quantities
    for (const l of draftLines) {
      if (!l.description.trim()) {
        toast({ title: 'Each line needs a description', variant: 'destructive' });
        return;
      }
      const q = Number(l.quantity_ordered);
      if (!q || q <= 0) {
        toast({ title: `Quantity required for "${l.description}"`, variant: 'destructive' });
        return;
      }
    }

    try {
      const poId = await createMut.mutateAsync({
        vendor_name: vendorName.trim(),
        expected_at: expectedAt ? new Date(expectedAt).toISOString() : null,
        notes: notes.trim() || null,
        lines: draftLines.map((l) => ({
          parts_line_id: l.parts_line_id,
          ticket_id: ticketId,
          job_id: l.job_id,
          description: l.description.trim(),
          vendor_part_number: l.vendor_part_number?.trim() || null,
          quantity_ordered: Number(l.quantity_ordered),
          unit_cost_ordered: l.unit_cost_ordered.trim() === '' ? null : Number(l.unit_cost_ordered),
          core_charge_ordered: Number(l.core_charge_ordered) || 0,
        })),
      });
      toast({
        title: 'Purchase order created',
        description: 'Opening PO…',
      });
      onOpenChange(false);
      onSuccess?.();
      navigate(`/purchasing/${poId}`);
    } catch (e: any) {
      toast({
        title: 'Failed to create PO',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create Purchase Order</DialogTitle>
          <DialogDescription>
            Creates a draft PO from the selected parts lines. You can edit quantities and unit costs before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="vendor">Vendor *</Label>
              <Input
                id="vendor"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="e.g. NAPA, WorldPac"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expected">Expected date</Label>
              <Input
                id="expected"
                type="date"
                value={expectedAt}
                onChange={(e) => setExpectedAt(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for this PO…"
              rows={2}
            />
          </div>

          <div>
            <div className="mb-2 text-sm font-medium">Lines ({draftLines.length})</div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Part #</TableHead>
                    <TableHead className="w-20 text-right">Qty</TableHead>
                    <TableHead className="w-28 text-right">Unit Cost</TableHead>
                    <TableHead className="w-24 text-right">Core</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draftLines.map((l, idx) => (
                    <TableRow key={l.parts_line_id}>
                      <TableCell>
                        <Input
                          value={l.description}
                          onChange={(e) => updateLine(idx, { description: e.target.value })}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={l.vendor_part_number ?? ''}
                          onChange={(e) => updateLine(idx, { vendor_part_number: e.target.value })}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={l.quantity_ordered}
                          onChange={(e) => updateLine(idx, { quantity_ordered: e.target.value })}
                          className="h-8 text-right tabular-nums"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.unit_cost_ordered}
                          onChange={(e) => updateLine(idx, { unit_cost_ordered: e.target.value })}
                          className="h-8 text-right tabular-nums"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.core_charge_ordered}
                          onChange={(e) => updateLine(idx, { core_charge_ordered: e.target.value })}
                          className="h-8 text-right tabular-nums"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-2 text-right text-sm">
              <span className="text-muted-foreground">Estimated total: </span>
              <span className="font-medium tabular-nums">
                {total.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create PO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
